import React, { useMemo, useState } from 'react';
import { Search, AlertTriangle, Clock, ArrowRight, Users, ArrowUpRight, ArrowDownRight, CheckCircle2, Download, Plus, FileText, GraduationCap, Link2 } from 'lucide-react';
import { MODULES, getModuleIcon } from '../../../content';
import { downloadCsv } from '../../../utils/csvExport';
import { formatRelativeTime } from '../../../utils/formatters';
import { GroupId } from '../../../config/systemConfig';
import { StudentData } from './StudentDetailPanel';
import { PageLayout } from '../../shared/PageLayout';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { EmptyState } from '../../../components/shared/EmptyState';
import { getAvatarGradient } from '../utils/avatarUtils';
import { needsAttention, getLearnerClassroomFlags } from '../utils/studentUtils';
import { useStore } from '../../../app/store';
import { type Classroom, type TeacherAccount } from '../../../accounts';
import { getWeeklyActivityForLearner, getLearnerTrendDelta, type DayActivity } from '../../../utils/activityHelpers';
import { DjayNarrator } from '../../../mascot';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { DataFreshness } from '../../shared/DataFreshness';
import { generateClassroomId, validateNewClassroom, canPerform } from '../../../accounts';
interface MyClassPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  classroomName?: string;
  onNavigate?: (route: string) => void;
  onSelectClassroom?: (id: string | null) => void;
}
type FilterType = 'all' | 'attention' | 'good' | 'inactive';
type SortType = 'name' | 'mastery' | 'activity';
export function MyClassPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent,
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  classroomName,
  onNavigate,
  onSelectClassroom
}: MyClassPageProps) {
  const {
    accounts
  } = useAppStore();
  const activeClassroom = useMemo(() => classrooms.find(c => c.id === selectedClassroomId) || null, [classrooms, selectedClassroomId]);

  /*
   ── Exporteren is eigenaar-alleen, en nu via de rechtentabel ───────────────
    `permissions.ts` legt `export_class_data` vast als `isClassroomOwner`. Die
   tabel had nul aanroepers, dus de knop stond feitelijk open voor co-docenten:
   het beleid stond op papier en de UI deed iets anders.
    Die geschreven regel is de juiste. Een CSV verlaat élke toegangscontrole die
   we hebben: geen RLS, niet in te trekken, geen spoor van wie hem opende, en
   hij overleeft het ontkoppelen van een co-docent. Dat is een andere soort
   handeling dan huiswerk klaarzetten — het is een verstrekking, en die hoort
   bij degene die de klas bezit.
    WAAROM `canPerform` EN NIET DE VERGELIJKING DIE HIER STOND
    `activeClassroom.teacherId === activeAccount.id` was correct, maar het is een
   tweede plek waar de regel staat. Bij de klasgrens van huiswerk bleek precies
   dat fataal: dezelfde beslissing stond op twee plekken, en op beide fout. Eén
   bron betekent ook dat een wijziging in de tabel hier vanzelf aankomt.
    Zonder klasselectie geen export: `targetClassroomId` is dan `undefined` en
   `isClassroomOwner` geeft false. Dat is de juiste faalstand — anders zou de
   knop "alle leerlingen die ik ooit heb gekoppeld" wegschrijven.
   */
  const isClassroomOwner = canPerform(activeAccount, 'export_class_data', {
    accounts,
    targetClassroomId: activeClassroom?.id
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [activeSort, setActiveSort] = useState<SortType>('name');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  // Inline classroom creation state
  const [newClassName, setNewClassName] = useState('');
  const [newGroupLevel, setNewGroupLevel] = useState(6);
  const [classWarnings, setClassWarnings] = useState<any[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const handleAddClassroom = () => {
    if (!newClassName.trim()) return;
    const warnings = validateNewClassroom(accounts, newClassName.trim(), activeAccount.id);
    const hasSameTeacherDuplicate = warnings.some(w => w.details.teacherId === activeAccount.id);
    if (hasSameTeacherDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    if (warnings.length > 0 && !confirmDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    const newClassroom: Classroom = {
      id: generateClassroomId(),
      name: newClassName.trim(),
      groupLevel: newGroupLevel,
      learnerIds: [],
      createdAt: Date.now(),
      teacherId: activeAccount.id,
      joinCode: '' // Auto-generated by addClassroom()
    };
    // `useAppStore` is een hook rond de Zustand-store, geen store zelf — de
    // oude `useAppStore.getState()` bestond niet en crashte zodra een
    // leerkracht een klas aanmaakte. `useStore` is de echte store.
    useStore.getState().addClassroom(activeAccount.id, newClassroom);
    setNewClassName('');
    setNewGroupLevel(6);
    setClassWarnings([]);
    setConfirmDuplicate(false);
  };
  // Enrich student data
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      let totalMastery = 0;
      let practicedCount = 0;
      let lowestMastery = 100;
      let highestMastery = 0;
      let weakestModule = null;
      let strongestModule = null;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          totalMastery += mastery;
          practicedCount++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
          if (mastery > highestMastery) {
            highestMastery = mastery;
            strongestModule = module;
          }
        }
      });
      const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
      const trendDelta = getLearnerTrendDelta(student.data.familyMastery || {}, selectedGroup);
      return {
        ...student,
        avgMastery,
        lowestMastery,
        weakestModule,
        strongestModule,
        weeklyActivity,
        daysPracticed,
        trendDelta,
        needsAttention: needsAttention(lowestMastery, practicedCount),
        isGood: avgMastery >= 70,
        isInactive: daysPracticed === 0
      };
    });
  }, [students, getModuleMastery, selectedGroup]);
  // Filter and sort
  const filteredAndSortedStudents = useMemo(() => {
    let result = enrichedStudents;
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(query));
    }
    // Filter
    switch (activeFilter) {
      case 'attention':
        result = result.filter(s => s.needsAttention);
        break;
      case 'good':
        result = result.filter(s => s.isGood);
        break;
      case 'inactive':
        result = result.filter(s => s.isInactive);
        break;
    }
    // Sort
    result.sort((a, b) => {
      switch (activeSort) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'mastery':
          return b.avgMastery - a.avgMastery;
        case 'activity':
          return b.daysPracticed - a.daysPracticed;
        default:
          return 0;
      }
    });
    return result;
  }, [enrichedStudents, searchQuery, activeFilter, activeSort]);
  // Get counts for filter tabs
  const counts = useMemo(() => {
    return {
      all: enrichedStudents.length,
      attention: enrichedStudents.filter(s => s.needsAttention).length,
      good: enrichedStudents.filter(s => s.isGood).length,
      inactive: enrichedStudents.filter(s => s.isInactive).length
    };
  }, [enrichedStudents]);
  const getMasteryColorClass = (mastery: number) => {
    if (mastery === 0) return 'text-slate-400';
    if (mastery >= 70) return 'text-emerald-600';
    if (mastery >= 40) return 'text-amber-600';
    return 'text-rose-600';
  };
  const isAllFilteredSelected = filteredAndSortedStudents.length > 0 && filteredAndSortedStudents.every(s => selectedIds.has(s.id));
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = new Set(selectedIds);
    if (e.target.checked) {
      filteredAndSortedStudents.forEach(s => newSelected.add(s.id));
    } else {
      filteredAndSortedStudents.forEach(s => newSelected.delete(s.id));
    }
    setSelectedIds(newSelected);
  };
  const handleExportCSV = () => {
    // Tweede linie naast de knop: een handler die zelf niet controleert, is
    // één hergebruik verwijderd van een open deur.
    if (!isClassroomOwner) return;
    const selectedStudents = enrichedStudents.filter(s => selectedIds.has(s.id));
    if (selectedStudents.length === 0) return;
    const headers = ['Naam', 'Beheersing (%)', 'Oefendagen', 'Trend (%)', 'Status'];
    const rows = selectedStudents.map(s => [s.name, s.avgMastery.toString(), s.daysPracticed.toString(), s.trendDelta.toString(), s.needsAttention ? 'Aandacht nodig' : s.isGood ? 'Goed bezig' : s.isInactive ? 'Inactief' : 'Bezig']);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    downloadCsv(csvContent, 'klas_export');
  };
  const summaryStats = useMemo(() => {
    if (enrichedStudents.length === 0) return null;
    const totalAvg = enrichedStudents.reduce((sum, s) => sum + s.avgMastery, 0);
    const classAvg = Math.round(totalAvg / enrichedStudents.length);
    let positiveTrend = 0;
    let negativeTrend = 0;
    enrichedStudents.forEach(s => {
      if (s.trendDelta > 0) positiveTrend++;else if (s.trendDelta < 0) negativeTrend++;
    });
    const attentionCount = enrichedStudents.filter(s => s.needsAttention).length;
    return {
      classAvg,
      positiveTrend,
      negativeTrend,
      attentionCount
    };
  }, [enrichedStudents]);
  const formatLastActive = (daysPracticed: number, weeklyActivity: DayActivity[]) => {
    if (daysPracticed === 0) return 'Nog niet';
    const recentDay = [...weeklyActivity].reverse().find(d => d.practiced);
    if (!recentDay) return 'Nog niet';
    return formatRelativeTime(new Date(recentDay.date).getTime());
  };
  return <PageLayout title="Mijn Klas" description={classroomName ? `Toont ${filteredAndSortedStudents.length} van ${students.length} leerlingen in ${classroomName}` : `Toont ${filteredAndSortedStudents.length} van ${students.length} leerlingen`} actions={classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-10357" data-mp="2jjhxpvfi78d7" /> : null} data-id="element-10356" data-mp="2t5soed0by7ki">
      
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-10358" data-mp="2sjehbin1dh1e">
        <div className="flex justify-end mb-[-12px]" data-id="element-10359" data-mp="2shtmdrwt7w30">
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={() => setLastLoadedAt(new Date())} data-id="element-10360" data-mp="2nllbwrtk4r26" />
          
        </div>

        {classrooms.length === 0 && <Card className="p-8 border-indigo-100 shadow-sm text-center bg-white max-w-2xl mx-auto mt-8" data-id="element-10361" data-mp="32bsoiqwvvfuy">
            <div className="flex justify-center mb-4" data-id="element-10362" data-mp="2xcz92sy1des5">
              <DjayNarrator size="md" emotion="excited" animate data-id="element-10363" data-mp="3bb7ape60p49n" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-10364" data-mp="379lsddaprxs2">
              Maak je eerste klas aan
            </h2>
            <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-10365" data-mp="3edk579jqi5p4">
              Maak een klas aan om leerlingen toe te voegen en hun voortgang te
              volgen.
            </p>

            <div className="max-w-sm mx-auto text-left space-y-4" data-id="element-10366" data-mp="3o95bvkzqzu57">
              <div data-id="element-10367" data-mp="3t0n51mvj4qfm">
                <label className="block text-sm font-bold text-slate-700 mb-1.5" data-id="element-10368" data-mp="3t15b1b7zuj0q">
                  Naam van de klas
                </label>
                <Input value={newClassName} onChange={e => {
              setNewClassName(e.target.value);
              setClassWarnings([]);
              setConfirmDuplicate(false);
            }} placeholder="Bijv. Groep 6A" className="w-full bg-slate-50 border-slate-200" data-id="element-10369" data-mp="z71kqz2pce4" />
              
              </div>

              <div data-id="element-10370" data-mp="fs0sr9npjgym">
                <label className="block text-sm font-bold text-slate-700 mb-1.5" data-id="element-10371" data-mp="2cmx06dwl5y4x">
                  Standaard niveau
                </label>
                <Select value={newGroupLevel} onChange={e => setNewGroupLevel(Number(e.target.value))} options={[{
              value: 6,
              label: 'Groep 6'
            }, {
              value: 7,
              label: 'Groep 7'
            }, {
              value: 8,
              label: 'Groep 8'
            }]} className="w-full bg-slate-50 border-slate-200" data-id="element-10372" data-mp="2hm9v7lxm2bil" />
              
              </div>

              {classWarnings.length > 0 && <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex gap-3" data-id="element-10373" data-mp="1hphl39e45fk6">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10374" data-mp="1dnweg5s69xif" />
                  <div data-id="element-10375" data-mp="1dm1tirgwi5ec">
                    <h4 className="font-bold text-amber-900 text-sm" data-id="element-10376" data-mp="18oa17wccgh9m">Let op</h4>
                    <ul className="mt-1 space-y-1" data-id="element-10377" data-mp="13pp0zwx3dxl6">
                      {classWarnings.map((w, i) => <li key={i} className="text-sm text-amber-800" data-id="element-10378" data-mp="13rt0m2nykafn" data-mpi={i}>
                          {w.message}
                        </li>)}
                    </ul>
                    {!classWarnings.some(w => w.details.teacherId === activeAccount.id) && !confirmDuplicate && <button onClick={() => setConfirmDuplicate(true)} className="mt-2 text-sm font-bold text-amber-700 hover:text-amber-800 underline" data-id="element-10379" data-mp="1g66ktwnsjful">
                  
                          Ja, ik wil deze naam toch gebruiken
                        </button>}
                  </div>
                </div>}

              <Button onClick={handleAddClassroom} disabled={!newClassName.trim()} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl mt-2" data-id="element-10380" data-mp="1c6flv5o41tez">
              
                <Plus className="w-4 h-4 mr-2" data-id="element-10381" data-mp="23vsk0k45xv2l" />
                Klas Aanmaken
              </Button>
            </div>
          </Card>}

        {/* Summary Strip */}
        {classrooms.length > 0 && summaryStats && <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-10382" data-mp="1uuuchruin5f9">
            <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-10383" data-mp="ehma3x8tvnkx">
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-10384" data-mp="ji07mlcszf4p">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-10385" data-mp="bx7hz1in55dv">
                  <CheckCircle2 className="w-5 h-5" data-id="element-10386" data-mp="6xbfx7ti7o0y" />
                </div>
                <div data-id="element-10387" data-mp="kynupfwxhjl9">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-10388" data-mp="kx2zrp6pbymv">
                    Gem. beheersing
                  </div>
                  <div className="text-2xl font-black text-slate-900" data-id="element-10389" data-mp="3oel59pz04tk4">
                    {summaryStats.classAvg}%
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-10390" data-mp="3kbg35oiu2tw0">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-10391" data-mp="20ur0behd5mt">
                  <ArrowUpRight className="w-5 h-5" data-id="element-10392" data-mp="3tc4hylg1wy9y" />
                </div>
                <div data-id="element-10393" data-mp="35mk065vmw729">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-10394" data-mp="35m0h2lu8zcmn">
                    Trend
                  </div>
                  <div className="text-2xl font-black text-slate-900 flex items-baseline gap-2" data-id="element-10395" data-mp="14lcbe6afwjko">
                    <span className="text-emerald-600 flex items-center" data-id="element-10396" data-mp="19jxd6a5cbbb8">
                      {summaryStats.positiveTrend}{' '}
                      <ArrowUpRight className="w-4 h-4 ml-0.5" data-id="element-10397" data-mp="1ee1kiuiiplcu" />
                    </span>
                    <span className="text-rose-600 flex items-center text-lg" data-id="element-10398" data-mp="1ja8fjs9dlcg0">
                      {summaryStats.negativeTrend}{' '}
                      <ArrowDownRight className="w-3 h-3 ml-0.5" data-id="element-10399" data-mp="qm49v6qt0zdb" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 flex items-center gap-4" data-id="element-10400" data-mp="vk5sjqkbiwo9">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0" data-id="element-10401" data-mp="znarjj56wg85">
                  <AlertTriangle className="w-5 h-5" data-id="element-10402" data-mp="2702wom0q9n62" />
                </div>
                <div data-id="element-10403" data-mp="2b16a71vaz744">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-10404" data-mp="2b0msnm9kekqm">
                    Aandacht nodig
                  </div>
                  <div className="text-2xl font-black text-slate-900" data-id="element-10405" data-mp="3riere5vhy705">
                    {summaryStats.attentionCount}
                  </div>
                </div>
              </div>
            </div>
          </Card>}

        {/* Controls */}
        {classrooms.length > 0 && students.length > 0 && <div className="flex flex-col gap-4" data-id="element-10406" data-mp="3cua6qwcvvleq">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center" data-id="element-10407" data-mp="3cv22q8ae7l5j">
              <div className="flex flex-wrap items-center gap-2" data-id="element-10408" data-mp="37ws7jxoo4kjx">
                <FilterButton active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} label="Alle" count={counts.all} color="indigo" data-id="element-10409" data-mp="33tvh5ksvzk5f" />
              
                <FilterButton active={activeFilter === 'attention'} onClick={() => setActiveFilter('attention')} label="Aandacht" count={counts.attention} color="rose" data-id="element-10410" data-mp="121frz6o3q82f" />
              
                <FilterButton active={activeFilter === 'good'} onClick={() => setActiveFilter('good')} label="Goed bezig" count={counts.good} color="emerald" data-id="element-10411" data-mp="5xa693w7b221" />
              
                <FilterButton active={activeFilter === 'inactive'} onClick={() => setActiveFilter('inactive')} label="Inactief" count={counts.inactive} color="amber" data-id="element-10412" data-mp="ayg1b8dc31eq" />
              
              </div>

              <div className="flex gap-3 w-full sm:w-auto items-center" data-id="element-10413" data-mp="14h0qdswrpow">
                <div className="relative flex-1 sm:w-48" data-id="element-10414" data-mp="3jalniluebmg0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-10415" data-mp="3ne9vv6quqm2b" />
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Zoeken..." className="pl-9 bg-white border-slate-200 rounded-xl w-full h-9 text-sm" data-id="element-10416" data-mp="39eilrh3ghptl" />
                
                </div>
                <Select value={activeSort} onChange={e => setActiveSort(e.target.value as SortType)} options={[{
              value: 'name',
              label: 'Naam'
            }, {
              value: 'mastery',
              label: 'Beheersing'
            }, {
              value: 'activity',
              label: 'Activiteit'
            }]} variant="admin" size="sm" fullWidth={false} className="bg-white border-slate-200 text-slate-700 font-medium rounded-xl h-9" data-id="element-10417" data-mp="17nxee8enbpqg" />
              
              </div>
            </div>
          </div>}

        {/* Compact Student Table */}
        {classrooms.length > 0 && (students.length === 0 ? <EmptyState title="Nog geen leerlingen" message="Voeg leerlingen toe aan deze klas om hun voortgang te volgen." emotion="thinking" colorScheme="indigo" actionLabel="Leerling Toevoegen" onAction={() => onNavigate?.('management')} data-id="element-10418" data-mp="3fw2aeikgn4ce" /> : filteredAndSortedStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white relative" data-id="element-10419" data-mp="3ktvg7ahupwc5">
              {/* Table header */}
              <div className="grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10420" data-mp="1vg8qkazn2pbo">
                <div className="w-6 flex items-center justify-center" data-id="element-10421" data-mp="3eljm0xd89e3m">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" checked={isAllFilteredSelected} onChange={handleSelectAll} data-id="element-10422" data-mp="3el9uv6gg50wc" />
              
                </div>
                <div className="w-8" data-id="element-10423" data-mp="30lijzel880mk"></div>
                <div data-id="element-10424" data-mp="30mjxdpppsz7g">Naam</div>
                <div className="hidden md:block w-32" data-id="element-10425" data-mp="30nv33v2q0h17">Zwakste vak</div>
                <div className="hidden md:block w-28" data-id="element-10426" data-mp="2vo8mrcrliko6">Laatst actief</div>
                <div className="text-right w-24" data-id="element-10427" data-mp="3ak831aouzr4e">Beheersing</div>
                <div className="w-6" data-id="element-10428" data-mp="35kv6fy86r5om"></div>
              </div>

              {/* Student rows */}
              <div className="divide-y divide-slate-100" data-id="element-10429" data-mp="35n7umt4jeca4">
                {filteredAndSortedStudents.map((student, i) => {
            const statusColor = student.needsAttention ? 'bg-rose-500' : student.isGood ? 'bg-emerald-500' : student.isInactive ? 'bg-amber-500' : 'bg-indigo-400';
            const statusLabel = student.needsAttention ? 'Aandacht nodig' : student.isGood ? 'Goed bezig' : student.isInactive ? 'Inactief' : 'Bezig';
            return <div key={student.id} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-10430" data-mp="jeila2bktu22" data-mpi={i}>
                  
                      {/* Checkbox */}
                      <div className="w-6 flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-10431" data-mp="1g5u6fg32ag0g">
                    
                        <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" checked={selectedIds.has(student.id)} onChange={e => {
                  const newSelected = new Set(selectedIds);
                  if (e.target.checked) {
                    newSelected.add(student.id);
                  } else {
                    newSelected.delete(student.id);
                  }
                  setSelectedIds(newSelected);
                }} data-id="element-10432" data-mp="16deqeobbs1i7" />
                    
                      </div>

                      {/* Avatar */}
                      <div className="relative" data-id="element-10433" data-mp="1uqycxwqndib3">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm`} data-id="element-10434" data-mp="1uq6od5vgwkub">
                      
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${statusColor}`} role="img" aria-label={statusLabel} title={statusLabel} data-id="element-10435" data-mp="1gza0fschkrtl" />
                    
                      </div>

                      {/* Name + coupling badges */}
                      <div className="min-w-0" data-id="element-10436" data-mp="122jasasm6uh6">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-10437" data-mp="rycbyfi7ca6x">
                          <div className="font-bold text-sm text-slate-900 truncate" data-id="element-10438" data-mp="s070s4wlgmga">
                            {student.name}
                          </div>
                          {(() => {
                    const flags = getLearnerClassroomFlags(accounts, student.id, activeClassroom);
                    const learnerAcc = accounts.accountsById[student.id];
                    const learnerGrade = learnerAcc && learnerAcc.role === 'learner' ? learnerAcc.grade : undefined;
                    return <>
                                {flags.gradeMismatch && learnerGrade && <span onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded" title={`Werkt op groep-${learnerGrade}-niveau (deze klas is groep ${activeClassroom?.groupLevel}).`} data-id="element-10439" data-mp="j3xzslsyxfs1">
                              
                                    <GraduationCap className="w-3 h-3" data-id="element-10440" data-mp="3rj7zjn9dy8wn" />
                                    Gr {learnerGrade}
                                  </span>}
                                {(flags.multiClassroom || flags.multiTeacher) && <span onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-sky-100 text-sky-700 text-[10px] font-bold rounded" title={flags.otherEnrollments.length > 0 ? 'Ook in: ' + flags.otherEnrollments.map(e => e.classroomName + ' (' + e.teacherName + ')').join(', ') : 'Gekoppeld aan meer dan een leerkracht'} data-id="element-10441" data-mp="26ux5ajglno3p">
                              
                                    <Link2 className="w-3 h-3" data-id="element-10442" data-mp="2ptj6dyc49f7m" />
                                    {flags.otherEnrollments.length > 0 ? '+' + flags.otherEnrollments.length : 'Multi'}
                                  </span>}
                              </>;
                  })()}
                        </div>
                      </div>

                      {/* Weakest Subject */}
                      <div className="hidden md:block w-32" data-id="element-10443" data-mp="qj05ilcmf7yw">
                        {student.weakestModule ? (() => {
                  const IconComp = getModuleIcon(student.weakestModule.icon);
                  return <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-medium text-slate-600 max-w-full hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors cursor-pointer" onClick={e => {
                    e.stopPropagation();
                    onSelectStudent(student.id);
                  }} data-id="element-10444" data-mp="374y6yz31vrat">
                          
                                <IconComp className="w-3 h-3 flex-shrink-0" data-id="element-10445" data-mp="29g4vzsvldpht" />
                                <span className="truncate" data-id="element-10446" data-mp="2ee6dwaha7irp">
                                  {student.weakestModule.title}
                                </span>
                              </div>;
                })() : <span className="text-slate-400 text-sm" data-id="element-10447" data-mp="cn305cc3ho2x">—</span>}
                      </div>

                      {/* Last Active */}
                      <div className="hidden md:block w-28 text-sm text-slate-500 font-medium" data-id="element-10448" data-mp="b6xowmr486fe">
                        {formatLastActive(student.daysPracticed, student.weeklyActivity)}
                      </div>

                      {/* Mastery */}
                      <div className="flex items-center justify-end gap-1.5 w-24" data-id="element-10449" data-mp="3tbir4rzwz5yx">
                        <span className={`font-black text-sm ${getMasteryColorClass(student.avgMastery)}`} data-id="element-10450" data-mp="3tdlmzsnqztbk">
                      
                          {student.avgMastery}%
                        </span>
                        {student.trendDelta !== 0 && <span className={student.trendDelta > 0 ? 'text-emerald-500' : 'text-rose-500'} title={`Trend: ${student.trendDelta > 0 ? '+' : ''}${student.trendDelta}%`} data-id="element-10451" data-mp="k62n1ryrlxr1">
                      
                            {student.trendDelta > 0 ? <ArrowUpRight className="w-3.5 h-3.5" data-id="element-10452" data-mp="1cog9kvenhxat" /> : <ArrowDownRight className="w-3.5 h-3.5" data-id="element-10453" data-mp="17w6p3uwb1pfx" />}
                          </span>}
                      </div>

                      {/* Arrow */}
                      <div className="text-slate-300 w-6 flex justify-end" data-id="element-10454" data-mp="2qkrd0umaw01h">
                        <ArrowRight className="w-4 h-4" data-id="element-10455" data-mp="2visujb42vrau" />
                      </div>
                    </div>;
          })}
              </div>
            </Card> : <EmptyState title="Geen leerlingen gevonden" message="Probeer je zoekopdracht of filters aan te passen." emotion="thinking" colorScheme="slate" data-id="element-10456" data-mp="2zlxsr1h4l6to" />)}
      </div>

      {/* Floating Action Bar */}
      {selectedIds.size > 0 && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-xl flex items-center gap-6 z-50 animate-in slide-in-from-bottom-10 fade-in duration-300" data-id="element-10457" data-mp="3egc3yfug7bxs">
          <div className="font-bold" data-id="element-10458" data-mp="3n9fheo53fosf">
            {selectedIds.size}{' '}
            {selectedIds.size === 1 ? 'leerling' : 'leerlingen'} geselecteerd
          </div>
          <div className="w-px h-6 bg-slate-700" data-id="element-10459" data-mp="11z4m9gtl4sl"></div>
          <div className="flex items-center gap-3" data-id="element-10460" data-mp="12h6pmq5yd8f">
            <button onClick={() => setSelectedIds(new Set())} className="text-sm font-medium text-slate-300 hover:text-white transition-colors" data-id="element-10461" data-mp="383y0wl8e72lo">
            
              Deselecteer alles
            </button>
            <Button onClick={() => onNavigate?.('reports')} size="sm" variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white border-slate-700" data-id="element-10462" data-mp="3h5e8kl7toqm7">
            
              <FileText className="w-4 h-4 mr-2" data-id="element-10463" data-mp="2pdq40kvad8dx" />
              Bekijk Rapport
            </Button>
            {isClassroomOwner ? <Button onClick={handleExportCSV} size="sm" className="bg-indigo-500 hover:bg-indigo-400 text-white border-none shadow-none" data-id="element-10464" data-mp="2kf5at5egboyz">
            
                <Download className="w-4 h-4 mr-2" data-id="element-10465" data-mp="2y84qb7e8hj6j" />
                Export CSV
              </Button> :
        /* Geen stille verdwijning: een co-docent die de knop kwijt is,
           zoekt hem — en de reden is niet te raden. */
        <span className="text-xs text-slate-400 max-w-[13rem] leading-snug" data-id="element-10466" data-mp="21odt9gkb8s9i">
                Exporteren kan alleen de eigenaar van deze klas.
              </span>}
          </div>
        </div>}
    </PageLayout>;
}
function FilterButton({
  active,
  onClick,
  label,
  count,
  color = 'indigo'
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  color?: 'indigo' | 'rose' | 'emerald' | 'amber';
}) {
  const colorClasses = {
    indigo: active ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    rose: active ? 'bg-rose-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    emerald: active ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
    amber: active ? 'bg-amber-500 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
  };
  const badgeClasses = {
    indigo: active ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500',
    rose: active ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500',
    emerald: active ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500',
    amber: active ? 'bg-amber-400 text-white' : 'bg-slate-100 text-slate-500'
  };
  return <button onClick={onClick} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${colorClasses[color]}`} data-id="element-10467" data-mp="hw92k622z0q">
      
      {label}
      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black leading-none ${badgeClasses[color]}`} data-id="element-10468" data-mp="33xuuej9kwyqx">
        
        {count}
      </span>
    </button>;
}